/**
 * Hook: useOnboarding
 *
 * O estado dos Primeiros Passos do contexto ativo (#387), para a página e para o aviso da Home.
 *
 * O cache é do contexto: ele guarda a chave a que pertence, e trocar de empresa, de clínica ou
 * para a plataforma recusa o que sobrou do contexto anterior (a empresa selecionada no topo é
 * o contexto ativo, e `useChaveDoContexto` já a carrega). Com resposta em cache a tela abre
 * já com ela e confere de novo em segundo plano — quem volta de cadastrar um funcionário não
 * pode ver o passo ainda pendente.
 *
 * Recusa do servidor não é falha: vira estado sem trilha nenhuma, como o painel da Home.
 */

import { useCallback, useEffect, useState } from 'react';

import { useChaveDoContexto } from '../stores/chaveDoContexto';
import { useManifestoStore } from '../stores/manifestoStore';
import { onboardingService } from '../services/onboardingService';
import { clearOnboardingCache, gravarCache, lerCache } from './onboardingCache';
import { motivoDaRecusa } from '../utils/acessoNegado';
import type { Perspectiva } from '../types/manifesto';
import type { ChaveDoContexto } from '../stores/chaveDoContexto';
import type { OnboardingStatus } from '../types/onboarding';

export type OnboardingState =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'pronto'; status: OnboardingStatus };

const CARREGANDO: OnboardingState = { estado: 'carregando' };
const ERRO: OnboardingState = { estado: 'erro' };

export { clearOnboardingCache };

function semTrilhas(perspectiva: Perspectiva): OnboardingStatus {
  return {
    perspectiva,
    context: { name: '', companies: 0, providers: 0 },
    tracks: [],
    notices: [],
  };
}

interface Guardado {
  contexto: ChaveDoContexto;
  resultado: OnboardingState;
}

export function useOnboarding(): OnboardingState & { recarregar: () => void } {
  const contexto = useChaveDoContexto();
  const [tentativa, setTentativa] = useState(0);
  const [guardado, setGuardado] = useState<Guardado | null>(null);

  // O que a tela mostra é derivado: resultado guardado só vale para o contexto que o pediu,
  // e na falta dele vale o cache do contexto de agora, ou o carregando.
  const emCache = lerCache(contexto);
  const resultado: OnboardingState =
    guardado?.contexto === contexto
      ? guardado.resultado
      : emCache
        ? { estado: 'pronto', status: emCache }
        : CARREGANDO;

  useEffect(() => {
    let ativo = true;

    onboardingService
      .getStatus()
      .then((status) => {
        gravarCache(contexto, status);
        if (ativo) setGuardado({ contexto, resultado: { estado: 'pronto', status } });
      })
      .catch((erro: unknown) => {
        if (!ativo) return;
        if (motivoDaRecusa(erro)) {
          // Lida na hora da recusa: a consulta não se refaz quando a perspectiva muda sozinha.
          const perspectiva = useManifestoStore.getState().manifesto?.perspectiva ?? 'empresa';
          setGuardado({ contexto, resultado: { estado: 'pronto', status: semTrilhas(perspectiva) } });
        } else if (!lerCache(contexto)) {
          setGuardado({ contexto, resultado: ERRO });
        }
      });

    return () => {
      ativo = false;
    };
  }, [contexto, tentativa]);

  const recarregar = useCallback(() => {
    setGuardado({ contexto, resultado: CARREGANDO });
    setTentativa((n) => n + 1);
  }, [contexto]);

  return { ...resultado, recarregar };
}

export default useOnboarding;
